/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import {
  CrudTemplate,
  ListColumns,
  CrudListType,
  CrudEditType,
  CrudRemoveType,
  CrudAddType
} from '../../../organisms/crud/form-types';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { FormList } from 'app/components/organisms/crud/form-types';
import { omit } from 'lodash';
import React from 'react';

/**
 * `IgnoreUrlLog` type.
 */

type IgnoreUrlLog = {
  id: string;
  url: string;
  description?: string;
  createdAt: string;
};

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns<IgnoreUrlLog> => [
  {
    title: 'URL',
    dataIndex: 'url',
    key: 'url',
    size: '2fr'
  },
  {
    title: translate('common.table.columns.description'),
    dataIndex: 'description',
    key: 'description',
    size: '3fr'
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  }
];

/**
 * Form fields.
 */

const formFields = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'url',
    label: 'URL',
    rules: { required: translate('common.errors.required') },
    placeholder: '/path/to/ignore'
  },
  {
    type: 'inputField',
    name: 'description',
    label: translate('common.labels.description'),
    placeholder: translate('common.placeholders.description'),
    rules: { required: false }
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<IgnoreUrlLog> {
  return {
    columns: listColumns(translate),
    route: appRoutes.dashboard.developer.ignoreUrlLogs,
    addButton: translate('common.actions.add'),
    canEdit: true,
    canRemove: true,
    key: ['ignore-url-logs'],
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100]
    },
    endpoint: apiEndpoints.ignoreUrlLogs,
    endpointById: apiEndpoints.ignoreUrlLogsById
  };
}

/**
 * Add.
 */

function add(translate: TFunction): CrudAddType<IgnoreUrlLog> {
  return {
    formFields: formFields(translate),
    endpoint: apiEndpoints.ignoreUrlLogs,
    route: `${appRoutes.dashboard.developer.ignoreUrlLogs}/add`,
    submitLabel: translate('common.actions.add'),
    normalizePayload: (values: any) => {
      return omit(values, ['id', 'createdAt', 'updatedAt']);
    }
  };
}

/**
 * Edit.
 */

function edit(translate: TFunction): CrudEditType<IgnoreUrlLog> {
  return {
    formFields: formFields(translate),
    endpoint: apiEndpoints.ignoreUrlLogsById,
    route: `${appRoutes.dashboard.developer.ignoreUrlLogs}/edit/:id`,
    submitLabel: translate('common.actions.edit'),
    normalizePayload: (values: any) => {
      return omit(values, ['id', 'createdAt', 'updatedAt']);
    }
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<IgnoreUrlLog> => ({
  endpoint: apiEndpoints.ignoreUrlLogsById
});

/**
 * Export `createIgnoreUrlLogsTemplate` function.
 */

export function createIgnoreUrlLogsTemplate(translate: TFunction): CrudTemplate<IgnoreUrlLog> {
  return {
    list: list(translate),
    add: add(translate),
    edit: edit(translate),
    remove: remove()
  };
}
